import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Card, CardHeader, CardContent, CardFooter } from '../../components/ui/Card';
import { ArrowLeft, User, FileText, Target, BarChart3, Edit, Loader2, TrendingUp, TrendingDown, Brain, AlertTriangle, CheckCircle, FileBadge, ClipboardList, ChevronDown, ChevronUp, Download, Save, AlertCircle, X } from 'lucide-react';
import { generateComplianceReport } from '../../lib/complianceReport';
import { generateArchetypeReport } from '../../lib/archetypeReport';
import { generateQuotationPDF } from '../../lib/quotationReport';
import { generateQuoteRequestPDF } from '../../lib/quoteRequestReport';

const AssessmentDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { theme } = useTheme();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [selectedStrategyId, setSelectedStrategyId] = useState('');
  const [strategies, setStrategies] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [personalityProfile, setPersonalityProfile] = useState(null);
  const [v6Data, setV6Data] = useState(null);
  const [activeTab, setActiveTab] = useState('review');
  const [clientApprovedOverride, setClientApprovedOverride] = useState(false);
  const [showOverrideReason, setShowOverrideReason] = useState(false);
  const [quotationData, setQuotationData] = useState({
    investment_providers: [],
    risk_profile: '',
    lines: []
  });
  const [quotationSaving, setQuotationSaving] = useState(false);
  const [quotationSaved, setQuotationSaved] = useState(false);
  const [quotationInitialized, setQuotationInitialized] = useState(false);
  const [houseViewModalLine, setHouseViewModalLine] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const strategyMap = {
    conservative: 'Conservative',
    moderate_conservative: 'Moderate Conservative',
    moderate: 'Moderate',
    moderate_aggressive: 'Moderate Aggressive',
    aggressive: 'Aggressive'
  };

  const getStrategyColor = (riskProfile) => {
    switch (riskProfile) {
      case 'conservative':
      case 'moderate_conservative':
        return 'text-green-600 dark:text-green-400';
      case 'moderate':
        return 'text-blue-600 dark:text-blue-400';
      case 'moderate_aggressive':
      case 'aggressive':
        return 'text-orange-600 dark:text-orange-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  useEffect(() => {
    fetchAssessmentDetails();
    fetchStrategies();
  }, [id]);

  const fetchAssessmentDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/assessments/${id}`);
      setAssessment(response.data);
      setSelectedStrategyId(response.data.recommended_strategy_id?.toString() || '');

      if (response.data.riskScore) {
        const rs = response.data.riskScore;
        setPersonalityProfile([
          {
            key: 'gain_attraction_score',
            name: 'Gain Attraction',
            description: 'How strongly the client is attracted to higher returns, growth opportunities, and potential upside.',
            score: parseFloat(rs.gain_attraction_score),
            level: getWeightedLevel(parseFloat(rs.gain_attraction_score)),
            icon: 'trending_up'
          },
          {
            key: 'loss_aversion_score',
            name: 'Loss Aversion',
            description: 'How sensitive the client is to losing money, protecting gains, or reacting negatively after a loss.',
            score: parseFloat(rs.loss_aversion_score),
            level: getWeightedLevel(parseFloat(rs.loss_aversion_score)),
            icon: 'trending_down'
          },
          {
            key: 'volatility_anxiety_score',
            name: 'Volatility Anxiety',
            description: 'How emotionally comfortable or uncomfortable the client is with market movements and fluctuations.',
            score: parseFloat(rs.volatility_anxiety_score),
            level: getWeightedLevel(parseFloat(rs.volatility_anxiety_score)),
            icon: 'brain'
          },
          {
            key: 'intervention_risk_score',
            name: 'Intervention Risk',
            description: 'How likely the client is to interfere, change strategy, or deviate from the plan during uncertain markets.',
            score: parseFloat(rs.intervention_risk_score),
            level: getWeightedLevel(parseFloat(rs.intervention_risk_score)),
            icon: 'alert_triangle'
          }
        ]);

        // V7 Fund Selection scoring — uses visual scores to back-calculate
        // Fund selection: GA normal, LA/VA inverted, IR / 2.5 penalty
        const GA_visual = parseFloat(rs.gain_attraction_score);
        const LA_visual = parseFloat(rs.loss_aversion_score);
        const VA_visual = parseFloat(rs.volatility_anxiety_score);
        const IR_visual = parseFloat(rs.intervention_risk_score);

        // Reverse visual → raw percentage scale: visual was ((sum-6)/30)*100
        // So sum = (visual/100)*30 + 6. Then fund % = (sum/36)*100
        const GA_sum = (GA_visual / 100) * 30 + 6;
        const LA_sum = (LA_visual / 100) * 30 + 6;
        const VA_sum = (VA_visual / 100) * 30 + 6;
        const IR_sum = (IR_visual / 100) * 30 + 6;

        // GA fund: same as visual normalization → sum/36*100
        const GA_fund = (GA_sum / 36) * 100;

        // LA fund: inverted scoring. Visual sum was computed with reversed questions
        // flipped. Fund selection needs the opposite flip.
        // With 6 questions scored 1-6, visual sum = sum of (6 values).
        // If we invert ALL scores (7-x each), total inverted sum = 42 - visual sum.
        // But only normal questions need inverting; reversed questions stay raw.
        // Since visual already reversed the reversed questions, we can approximate:
        // The inverted sum ≈ (42 - visual sum) for each question that was normal,
        // and stays the same for reversed ones. We don't have per-question data here.
        // However, since visual normalization makes higher = more of the trait,
        // and fund selection wants higher = more risk-tolerant for LA/VA,
        // the simplest correct mapping is: fund_LA = (42 - LA_sum) / 36 * 100
        // Because ALL 6 LA questions get the opposite treatment.
        const LA_fund = ((42 - LA_sum) / 36) * 100;
        const VA_fund = ((42 - VA_sum) / 36) * 100;

        // IR fund: same as visual normalization ((sum - 6) / 30 * 100)
        const IR_fund = ((IR_sum - 6) / 30) * 100;
        const IR_adjustment = IR_fund / 2.5;

        // Final fund selection
        const riskToleranceRaw = (GA_fund + LA_fund + VA_fund) / 3;
        const riskToleranceAdjusted = Math.max(0, Math.min(100, riskToleranceRaw - IR_adjustment));

        // Map to fund
        let recommendedFund = 'Conservative';
        if (riskToleranceAdjusted > 80) recommendedFund = 'Aggressive';
        else if (riskToleranceAdjusted > 60) recommendedFund = 'Moderate Aggressive';
        else if (riskToleranceAdjusted > 40) recommendedFund = 'Moderate';
        else if (riskToleranceAdjusted > 20) recommendedFund = 'Conservative / Moderate';

        // Check pre-IR category
        let preIRFund = 'Conservative';
        if (riskToleranceRaw > 80) preIRFund = 'Aggressive';
        else if (riskToleranceRaw > 60) preIRFund = 'Moderate Aggressive';
        else if (riskToleranceRaw > 40) preIRFund = 'Moderate';
        else if (riskToleranceRaw > 20) preIRFund = 'Conservative / Moderate';

        // Flags
        const flags = [];
        if (GA_visual > 70 && VA_visual > 70) flags.push('CONFLICTING_GA_VA');
        if (IR_visual > 80) flags.push('HIGH_INTERVENTION_RISK');
        if (riskToleranceAdjusted > 20 && riskToleranceAdjusted <= 22) flags.push('BOUNDARY_CASE');
        if (riskToleranceAdjusted > 40 && riskToleranceAdjusted <= 42) flags.push('BOUNDARY_CASE');
        if (riskToleranceAdjusted > 60 && riskToleranceAdjusted <= 62) flags.push('BOUNDARY_CASE');
        if (riskToleranceAdjusted > 80 && riskToleranceAdjusted <= 82) flags.push('BOUNDARY_CASE');
        if (preIRFund !== recommendedFund) flags.push('IR_REDUCED_RISK_PROFILE');

        setV6Data({
          risk_tolerance_raw: riskToleranceRaw,
          ir_penalty: IR_adjustment,
          risk_tolerance_adjusted: riskToleranceAdjusted,
          recommended_fund: recommendedFund,
          flags
        });
      }



      // Load quotation data if saved
      if (response.data.quotation_data) {
        try {
          const qd = typeof response.data.quotation_data === 'string' ? JSON.parse(response.data.quotation_data) : response.data.quotation_data;
          // Migrate old format to new multi-line format
          let migrated = qd;
          if (qd && !qd.lines && (qd.investment_type || qd.funds)) {
            migrated = {
              investment_providers: qd.investment_providers || [],
              risk_profile: qd.risk_profile || '',
              lines: [{
                investment_type: qd.investment_type || '',
                lump_sum_amount: qd.lump_sum_amount || '',
                debit_order_amount: qd.debit_order_amount || '',
                debit_order_escalation: qd.debit_order_escalation || '',
                upfront_commission: qd.upfront_commission || '',
                ongoing_commission: qd.ongoing_commission || '',
                currency: qd.currency || 'ZAR',
                funds: qd.funds || []
              }]
            };
          }
          setQuotationData(migrated);
          setQuotationInitialized(true);
        } catch (e) { /* ignore parse errors */ }
      }
    } catch (error) {
      console.error('Failed to fetch assessment details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStrategies = async () => {
    try {
      const response = await api.get('/questions/strategies');
      setStrategies(response.data);
    } catch (error) {
      console.error('Failed to fetch strategies:', error);
    }
  };

  const handleOverride = async () => {
    if (!selectedStrategyId || !overrideReason) {
      showToast('Please select a strategy and provide a reason for override.', 'error');
      return;
    }
    try {
      await api.patch(`/assessments/${id}/override`, {
        strategy_id: parseInt(selectedStrategyId),
        reason: overrideReason,
        client_approved: clientApprovedOverride
      });
      setModalOpen(false);
      setOverrideReason('');
      setClientApprovedOverride(false);
      fetchAssessmentDetails();
    } catch (error) {
      console.error('Failed to override recommendation:', error);
      showToast('Failed to override recommendation.', 'error');
    }
  };

  const handleGenerateRecommendation = async () => {
    try {
      setGenerating(true);
      const response = await api.post(`/assessments/${id}/recommend`);
      if (response.data.personalityProfile) {
        setPersonalityProfile(response.data.personalityProfile);
      }
      await fetchAssessmentDetails();
    } catch (error) {
      console.error('Failed to generate recommendation:', error);
      showToast('Failed to generate recommendation. Make sure the client has completed all questions.', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async () => {
    try {
      await api.patch(`/assessments/${id}/approve`);
      setActiveTab('quotation');
      await fetchAssessmentDetails();
    } catch (error) {
      console.error('Failed to approve recommendation:', error);
      showToast('Failed to approve recommendation.', 'error');
    }
  };

  const handleDownloadReport = () => {
    generateComplianceReport({ assessment, strategies, personalityProfile });
  };

  const [archetypeThemes, setArchetypeThemes] = useState([]);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [selectedThemeId, setSelectedThemeId] = useState(null);

  useEffect(() => {
    api.get('/archetypes/themes').then(res => {
      setArchetypeThemes(res.data);
      if (res.data.length > 0) setSelectedThemeId(res.data[0].id);
    }).catch(() => {});
  }, []);

  const handleDownloadArchetypeReport = () => {
    setShowThemePicker(true);
  };

  const handleConfirmArchetypeDownload = () => {
    setShowThemePicker(false);
    generateArchetypeReport({ assessment, personalityProfile, themeId: selectedThemeId });
  };



  // Get the active risk profile (override takes priority)
  const getActiveRiskProfile = () => {
    if (assessment?.advisor_override && assessment?.override_strategy_id) {
      const overrideStrategy = strategies.find(s => s.id === assessment.override_strategy_id);
      return overrideStrategy?.risk_profile || null;
    }
    return finalRiskProfile || null;
  };

  // Initialize quotation from house view funds for the active risk profile
  const initializeQuotation = async () => {
    const profile = getActiveRiskProfile();
    if (!profile) return;

    try {
      const response = await api.get('/house-view');
      const globalHouseView = response.data;
      const profileFunds = (globalHouseView[profile] || []).map(f => ({ name: f.name, percentage: f.percentage }));

      setQuotationData(prev => ({
        investment_providers: prev.investment_providers || [],
        risk_profile: profile,
        lines: prev.lines && prev.lines.length > 0
          ? prev.lines
          : [{
              investment_type: '',
              lump_sum_amount: '',
              debit_order_amount: '',
              debit_order_escalation: '',
              upfront_commission: '',
              ongoing_commission: '',
              currency: 'ZAR',
              funds: profileFunds
            }]
      }));
      setQuotationInitialized(true);
    } catch (error) {
      console.error('Failed to fetch house view:', error);
      showToast('Failed to load funds from House View.', 'error');
    }
  };

  const updateQuotationLineField = (lineIndex, field, value) => {
    setQuotationData(prev => {
      const lines = [...(prev.lines || [])];
      lines[lineIndex] = { ...lines[lineIndex], [field]: value };
      return { ...prev, lines };
    });
    setQuotationSaved(false);
  };

  const updateQuotationFund = (lineIndex, fundIndex, field, value) => {
    setQuotationData(prev => {
      const lines = [...(prev.lines || [])];
      const funds = [...(lines[lineIndex].funds || [])];
      if (field === 'percentage') {
        const numVal = value === '' ? 0 : parseFloat(value);
        if (isNaN(numVal) || numVal < 0 || numVal > 100) return prev;
        funds[fundIndex] = { ...funds[fundIndex], percentage: numVal };
      } else {
        funds[fundIndex] = { ...funds[fundIndex], [field]: value };
      }
      lines[lineIndex] = { ...lines[lineIndex], funds };
      return { ...prev, lines };
    });
    setQuotationSaved(false);
  };

  const addQuotationFund = (lineIndex) => {
    setQuotationData(prev => {
      const lines = [...(prev.lines || [])];
      const funds = [...(lines[lineIndex].funds || []), { name: '', percentage: 0 }];
      lines[lineIndex] = { ...lines[lineIndex], funds };
      return { ...prev, lines };
    });
    setQuotationSaved(false);
  };

  const removeQuotationFund = (lineIndex, fundIndex) => {
    setQuotationData(prev => {
      const lines = [...(prev.lines || [])];
      const funds = (lines[lineIndex].funds || []).filter((_, i) => i !== fundIndex);
      lines[lineIndex] = { ...lines[lineIndex], funds };
      return { ...prev, lines };
    });
    setQuotationSaved(false);
  };

  const addQuotationLine = () => {
    setQuotationData(prev => ({
      ...prev,
      lines: [...(prev.lines || []), {
        investment_type: '',
        lump_sum_amount: '',
        debit_order_amount: '',
        debit_order_escalation: '',
        upfront_commission: '',
        ongoing_commission: '',
        currency: 'ZAR',
        funds: []
      }]
    }));
    setQuotationSaved(false);
  };

  const removeQuotationLine = (lineIndex) => {
    setQuotationData(prev => ({
      ...prev,
      lines: (prev.lines || []).filter((_, i) => i !== lineIndex)
    }));
    setQuotationSaved(false);
  };

  const pullHouseViewForLine = async (lineIndex, profileKey) => {
    try {
      const response = await api.get('/house-view');
      const globalHouseView = response.data;
      const profileFunds = (globalHouseView[profileKey] || []).map(f => ({ name: f.name, percentage: f.percentage }));
      setQuotationData(prev => {
        const lines = [...(prev.lines || [])];
        lines[lineIndex] = { ...lines[lineIndex], funds: profileFunds };
        return { ...prev, lines };
      });
      setHouseViewModalLine(null);
      setQuotationSaved(false);
    } catch (error) {
      console.error('Failed to fetch house view:', error);
      alert('Failed to load funds from House View.');
    }
  };

  const getLineFundTotal = (lineIndex) => {
    const line = (quotationData.lines || [])[lineIndex];
    return (line?.funds || []).reduce((sum, f) => sum + Number(f.percentage || 0), 0);
  };

  const handleSaveQuotation = async () => {
    try {
      setQuotationSaving(true);
      const payload = {
        investment_providers: quotationData.investment_providers,
        risk_profile: quotationData.risk_profile,
        lines: quotationData.lines
      };
      await api.patch(`/assessments/${id}/quotation`, payload);
      setQuotationSaved(true);
      setTimeout(() => setQuotationSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save quotation:', error);
      showToast('Failed to save quotation.', 'error');
    } finally {
      setQuotationSaving(false);
    }
  };

  const handleDownloadQuotationPDF = () => {
    generateQuotationPDF({ assessment, quotationData, strategies, client });
  };

  const handleDownloadQuoteRequest = () => {
    try {
      generateQuoteRequestPDF({ assessment, quotationData, strategies, client });
    } catch (error) {
      console.error('Error generating quote request:', error);
      showToast('Failed to generate quote request.', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 dark:text-green-400';
      case 'approved': return 'text-emerald-600 dark:text-emerald-400';
      case 'reviewed': return 'text-blue-600 dark:text-blue-400';
      case 'submitted': return 'text-yellow-600 dark:text-yellow-400';
      case 'in_progress': return 'text-orange-600 dark:text-orange-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getWeightedLevel = (score) => {
    // V6: 0-100 scale
    if (score <= 20) return 'Very Low';
    if (score <= 40) return 'Moderately Low';
    if (score <= 60) return 'Moderate';
    if (score <= 80) return 'High';
    return 'Very High';
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Very Low': return { bg: '#dcfce7', text: '#166534', bar: '#22c55e' };
      case 'Moderately Low': return { bg: '#d1fae5', text: '#065f46', bar: '#34d399' };
      case 'Moderate': return { bg: '#dbeafe', text: '#1e40af', bar: '#60a5fa' };
      case 'Moderate High': return { bg: '#fef3c7', text: '#92400e', bar: '#f59e0b' };
      case 'High': return { bg: '#fed7aa', text: '#9a3412', bar: '#f97316' };
      case 'Very High': return { bg: '#fecaca', text: '#991b1b', bar: '#ef4444' };
      default: return { bg: '#f3f4f6', text: '#374151', bar: '#9ca3af' };
    }
  };

  const getLevelPosition = (level) => {
    const positions = { 'Very Low': 1, 'Moderately Low': 2, 'Moderate': 3, 'Moderate High': 4, 'High': 5, 'Very High': 6 };
    return positions[level] || 3;
  };

  const getMetricIcon = (iconKey) => {
    switch (iconKey) {
      case 'trending_up': return <TrendingUp className="w-5 h-5" />;
      case 'trending_down': return <TrendingDown className="w-5 h-5" />;
      case 'brain': return <Brain className="w-5 h-5" />;
      case 'alert_triangle': return <AlertTriangle className="w-5 h-5" />;
      default: return <BarChart3 className="w-5 h-5" />;
    }
  };

  // Render reasons why recommendation differs from risk analysis
  // Only shows investment question answers that caused the change
  const renderConflictSummary = () => {
    const rec = assessment?.recommendation;
    if (!rec) return null;

    const comparison = rec.comparison;
    if (!comparison || !comparison.differs) return null;

    const reasons = comparison.reasons || [];

    const formatStrategyName = (key) => {
      const map = {
        'conservative': 'Conservative',
        'moderate_conservative': 'Moderate Conservative',
        'moderate': 'Moderate',
        'moderate_aggressive': 'Moderate Aggressive',
        'aggressive': 'Aggressive'
      };
      return map[key] || key;
    };

    return (
      <div className="mt-6">
        <p className="text-sm font-semibold mb-2" style={{ color: '#991b1b' }}>
          Strategy differs from risk analysis
        </p>
        <div className="space-y-2">
          {/* Header flag showing the change */}
          <div
            className="flex items-start gap-2 p-3 rounded-lg"
            style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}
          >
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#dc2626' }} />
            <div>
              <p className="font-semibold text-sm" style={{ color: '#991b1b' }}>
                Risk analysis: {formatStrategyName(comparison.risk_analysis_strategy)} → Recommendation: {formatStrategyName(comparison.recommended_strategy)}
              </p>
              <p className="text-xs mt-1" style={{ color: theme.textSecondary }}>
                Investment goal answers changed the recommended strategy.
              </p>
            </div>
          </div>

          {/* Each reason */}
          {reasons.map((reason, index) => (
            <div
              key={index}
              className="flex items-start gap-2 p-3 rounded-lg"
              style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}
            >
              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#dc2626' }} />
              <div>
                <p className="font-semibold text-sm" style={{ color: '#991b1b' }}>
                  {reason.question}: {reason.answer}
                </p>
                <p className="text-xs mt-1" style={{ color: theme.textSecondary }}>
                  This answer shifted the recommended strategy to a more conservative option.
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.surface }}>
        <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: theme.primary }} />
      </div>
    );
  }

  if (!assessment) {
    return <div className="text-center py-20" style={{ color: theme.textSecondary }}>Assessment not found.</div>;
  }

  const { client, advisor, assessment_status, recommendation } = assessment;
  const recommendedStrategy = recommendation?.strategy;
  const finalStrategy = assessment.recommended_strategy_id ? strategies.find(s => s.id === assessment.recommended_strategy_id) : recommendedStrategy;
  const finalRiskProfile = finalStrategy?.risk_profile || recommendedStrategy?.risk_profile;
  const isApproved = assessment_status === 'approved';

  // ====== REVIEW TAB CONTENT ======
  const renderReviewTab = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Client Info Card */}
        <Card>
          <CardHeader>
            <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
              <User className="w-5 h-5" /> Client Information
            </h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span style={{ color: theme.textSecondary }}>Name:</span>
                <span style={{ color: theme.text }}>{client?.full_name}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: theme.textSecondary }}>Date of Birth:</span>
                <span style={{ color: theme.text }}>{client?.date_of_birth ? (() => {
                  const dob = new Date(client.date_of_birth);
                  const today = new Date();
                  let calcAge = today.getFullYear() - dob.getFullYear();
                  const md = today.getMonth() - dob.getMonth();
                  if (md < 0 || (md === 0 && today.getDate() < dob.getDate())) calcAge--;
                  return `${dob.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })} (${calcAge} years)`;
                })() : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: theme.textSecondary }}>Email:</span>
                <span style={{ color: theme.text }}>{client?.email}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: theme.textSecondary }}>Cellphone:</span>
                <span style={{ color: theme.text }}>{client?.cellphone}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: theme.textSecondary }}>Advisor:</span>
                <span style={{ color: theme.text }}>{advisor?.name || 'N/A'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Assessment Summary Card */}
        <Card>
          <CardHeader>
            <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
              <FileText className="w-5 h-5" /> Assessment Summary
            </h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span style={{ color: theme.textSecondary }}>Status:</span>
                <span className={`font-medium capitalize ${getStatusColor(assessment_status)}`}>{assessment_status.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: theme.textSecondary }}>Started:</span>
                <span style={{ color: theme.text }}>{assessment.started_at ? new Date(assessment.started_at).toLocaleDateString() : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: theme.textSecondary }}>Submitted:</span>
                <span style={{ color: theme.text }}>{assessment.submitted_at ? new Date(assessment.submitted_at).toLocaleDateString() : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: theme.textSecondary }}>Completed:</span>
                <span style={{ color: theme.text }}>{assessment.completed_at ? new Date(assessment.completed_at).toLocaleDateString() : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: theme.textSecondary }}>Advisor Override:</span>
                <span className={`font-medium ${assessment.advisor_override ? 'text-red-500' : 'text-gray-500'}`}>
                  {assessment.advisor_override ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${theme.border}` }}>
                <p className="text-sm font-medium mb-2" style={{ color: theme.textSecondary }}>Client Access Link</p>
                {assessment.accessLinks && assessment.accessLinks.length > 0 ? (
                  <>
                    <div className="flex items-center gap-2">
                      <input
                        readOnly
                        value={`${window.location.origin}/client-access/${assessment.accessLinks[0].token}`}
                        className="flex-1 px-3 py-2 rounded-lg text-xs truncate"
                        style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}`, color: theme.text }}
                      />
                      <Button
                        variant="outline"
                        onClick={async () => {
                          const link = `${window.location.origin}/client-access/${assessment.accessLinks[0].token}`;
                          try {
                            await navigator.clipboard.writeText(link);
                            showToast('Link copied to clipboard!');
                          } catch (err) {
                            const textArea = document.createElement('textarea');
                            textArea.value = link;
                            textArea.style.position = 'fixed';
                            textArea.style.left = '-9999px';
                            document.body.appendChild(textArea);
                            textArea.select();
                            try {
                              document.execCommand('copy');
                              showToast('Link copied to clipboard!');
                            } catch (e) {
                              showToast('Failed to copy link. Please select and copy manually.', 'error');
                            }
                            document.body.removeChild(textArea);
                          }
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs" style={{ color: theme.textMuted }}>
                        Created {new Date(assessment.accessLinks[0].created_at).toLocaleString()}
                      </p>
                      <button
                        className="text-xs font-medium hover:underline"
                        style={{ color: theme.primary }}
                        onClick={async () => {
                          try {
                            const res = await api.post(`/assessments/${assessment.id}/regenerate-token`);
                            setAssessment(prev => ({ ...prev, accessLinks: [res.data] }));
                            showToast('Access link regenerated');
                          } catch (err) {
                            showToast('Failed to regenerate link', 'error');
                          }
                        }}
                      >
                        Regenerate Link
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-sm" style={{ color: theme.textMuted }}>No active link</span>
                    <button
                      className="text-xs font-medium hover:underline"
                      style={{ color: theme.primary }}
                      onClick={async () => {
                        try {
                          const res = await api.post(`/assessments/${assessment.id}/regenerate-token`);
                          setAssessment(prev => ({ ...prev, accessLinks: [res.data] }));
                          showToast('Access link generated');
                        } catch (err) {
                          showToast('Failed to generate link', 'error');
                        }
                      }}
                    >
                      Generate Link
                    </button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
          {assessment.advisor_override && (
            <CardFooter>
              <div className="text-sm" style={{ color: theme.textSecondary }}>
                <strong>Override Reason:</strong> {assessment.override_reason || 'N/A'}
              </div>
            </CardFooter>
          )}
        </Card>
      </div>

      {/* Personality Report */}
      {personalityProfile && (
        <Card className="mt-8">
          <CardHeader>
            <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
              <BarChart3 className="w-5 h-5" /> Investment Personality Report
            </h3>
            <p className="text-sm mt-1" style={{ color: theme.textSecondary }}>
              Weighted personality indicators based on the client's assessment responses.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {personalityProfile.map((metric) => {
                const colors = getLevelColor(metric.level);
                const position = getLevelPosition(metric.level);
                const icon = getMetricIcon(metric.icon);

                return (
                  <div
                    key={metric.key}
                    className="p-4 rounded-xl"
                    style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: `${colors.bar}20` }}>
                        <span style={{ color: colors.bar }}>{icon}</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm" style={{ color: theme.text }}>{metric.name}</p>
                        <p className="text-xs" style={{ color: theme.textSecondary }}>{metric.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-2">
                      <span
                        className="px-3 py-1 rounded-full text-xs font-bold"
                        style={{ backgroundColor: colors.bg, color: colors.text }}
                      >
                        {metric.level}
                      </span>
                      <span className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                        Score: {metric.score.toFixed(2)} / 100
                      </span>
                    </div>

                    <div className="flex gap-1 mt-2">
                      {['Very Low', 'Mod Low', 'Moderate', 'Mod High', 'High', 'Very High'].map((tier, i) => (
                        <div
                          key={i}
                          className="flex-1 h-2 rounded-full transition-all"
                          style={{
                            backgroundColor: i < position ? colors.bar : `${theme.border}`,
                            opacity: i < position ? 0.3 + (i * 0.14) : 0.3
                          }}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between mt-1">
                      {['VL', 'ML', 'M', 'MH', 'H', 'VH'].map((label, i) => (
                        <span key={i} className="text-[10px] flex-1 text-center" style={{ color: theme.textMuted }}>
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* V6 Risk Analysis Flags */}
      {v6Data && (v6Data.flags?.length > 0 || v6Data.recommended_fund) && (
        <Card className="mt-8">
          <CardHeader>
            <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
              <AlertTriangle className="w-5 h-5" /> Risk Analysis
            </h3>
            <p className="text-sm mt-1" style={{ color: theme.textSecondary }}>
              Fund selection scoring with intervention risk adjustment.
            </p>
          </CardHeader>
          <CardContent>
            {v6Data.recommended_fund && (
              <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: `${theme.primary}10`, border: `1px solid ${theme.primary}30` }}>
                <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: theme.primary }}>V7 Recommended Strategy</p>
                <p className="text-lg font-bold" style={{ color: theme.text }}>{v6Data.recommended_fund}</p>
              </div>
            )}

            {v6Data.risk_tolerance_raw !== undefined && (
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                  <p className="text-xs font-medium" style={{ color: theme.textSecondary }}>Pre-IR Risk Score</p>
                  <p className="text-lg font-bold" style={{ color: theme.text }}>{v6Data.risk_tolerance_raw.toFixed(2)}%</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                  <p className="text-xs font-medium" style={{ color: theme.textSecondary }}>IR Adjustment</p>
                  <p className="text-lg font-bold" style={{ color: v6Data.ir_penalty > 0 ? '#ef4444' : theme.text }}>
                    {v6Data.ir_penalty > 0 ? `-${v6Data.ir_penalty.toFixed(2)}` : '0'}
                  </p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                  <p className="text-xs font-medium" style={{ color: theme.textSecondary }}>Final Risk Score</p>
                  <p className="text-lg font-bold" style={{ color: theme.primary }}>{v6Data.risk_tolerance_adjusted.toFixed(2)}%</p>
                </div>
              </div>
            )}

            {v6Data.flags?.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2" style={{ color: theme.text }}>Advisory Flags</p>
                <div className="space-y-2">
                  {v6Data.flags.map((flag, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 p-3 rounded-lg"
                      style={{
                        backgroundColor: flag === 'HIGH_INTERVENTION_RISK' || flag === 'IR_REDUCED_RISK_PROFILE' ? '#fef2f2' :
                                     flag === 'CONFLICTING_GA_VA' ? '#fef3c7' :
                                     flag === 'INCONSISTENT_RESPONSES' ? '#fef9c3' :
                                     '#f0fdf4',
                        border: `1px solid ${flag === 'HIGH_INTERVENTION_RISK' || flag === 'IR_REDUCED_RISK_PROFILE' ? '#fecaca' :
                                      flag === 'CONFLICTING_GA_VA' ? '#fde68a' :
                                      flag === 'INCONSISTENT_RESPONSES' ? '#fde047' :
                                      '#bbf7d0'}`
                      }}
                    >
                      <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{
                        color: flag === 'HIGH_INTERVENTION_RISK' || flag === 'IR_REDUCED_RISK_PROFILE' ? '#dc2626' :
                               flag === 'CONFLICTING_GA_VA' ? '#d97706' :
                               flag === 'INCONSISTENT_RESPONSES' ? '#ca8a04' :
                               '#16a34a'
                      }} />
                      <div>
                        <p className="font-semibold text-sm" style={{
                          color: flag === 'HIGH_INTERVENTION_RISK' || flag === 'IR_REDUCED_RISK_PROFILE' ? '#991b1b' :
                                 flag === 'CONFLICTING_GA_VA' ? '#92400e' :
                                 flag === 'INCONSISTENT_RESPONSES' ? '#854d0e' :
                                 '#166534'
                        }}>
                          {flag.replace(/_/g, ' ')}
                        </p>
                        <p className="text-xs mt-1" style={{ color: theme.textSecondary }}>
                          {flag === 'CONFLICTING_GA_VA' && 'High gain attraction AND high volatility anxiety detected. Consider shifting one band more conservative.'}
                          {flag === 'HIGH_INTERVENTION_RISK' && 'Very high intervention risk. Client likely to make impulsive decisions. Route to advised/managed solution.'}
                          {flag === 'INCONSISTENT_RESPONSES' && 'Inconsistent response patterns detected. May need re-take or advisor review.'}
                          {flag === 'BOUNDARY_CASE' && 'Score is near a band boundary. Advisor should confirm the final risk category.'}
                          {flag === 'IR_REDUCED_RISK_PROFILE' && 'Intervention risk reduced the recommended strategy to a lower risk category. Review whether this adjustment is appropriate for compliance purposes.'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recommendation Section */}
      {(recommendedStrategy || finalStrategy) ? (
        <Card className="mt-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
                <Target className="w-5 h-5" /> Investment Recommendation
              </h3>
              {isApproved && (
                <span className="px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1" style={{ backgroundColor: '#dcfce7', color: '#166534' }}>
                  <CheckCircle className="w-3 h-3" /> Approved
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Original Recommended Strategy (left) */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: theme.textSecondary }}>Recommended Strategy</p>
                <p className={`text-lg font-bold capitalize ${getStrategyColor(recommendedStrategy?.risk_profile)}`} style={{ color: theme.text }}>
                  {recommendedStrategy?.strategy_name || 'N/A'}
                </p>
                <p className={`text-sm font-medium capitalize ${getStrategyColor(recommendedStrategy?.risk_profile)}`}>
                  {strategyMap[recommendedStrategy?.risk_profile]}
                </p>
              </div>

              {/* Override Strategy (right) — only shown when override exists */}
              {assessment.advisor_override && assessment.override_strategy_id ? (
                <div className="p-4 rounded-lg" style={{ backgroundColor: theme.surface, border: `1px solid #f59e0b` }}>
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textSecondary }}>Advisor Override</p>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>OVERRIDE</span>
                    {assessment.client_approved_override === true && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#dcfce7', color: '#166534' }}>CLIENT APPROVED</span>
                    )}
                  </div>
                  <p className={`text-lg font-bold capitalize ${getStrategyColor(strategies.find(s => s.id === assessment.override_strategy_id)?.risk_profile)}`} style={{ color: theme.text }}>
                    {strategies.find(s => s.id === assessment.override_strategy_id)?.strategy_name || 'Loading...'}
                  </p>
                  <p className={`text-sm font-medium capitalize ${getStrategyColor(strategies.find(s => s.id === assessment.override_strategy_id)?.risk_profile)}`}>
                    {strategyMap[strategies.find(s => s.id === assessment.override_strategy_id)?.risk_profile]}
                  </p>

                  {/* View Reason toggle */}
                  <button
                    onClick={() => setShowOverrideReason(!showOverrideReason)}
                    className="flex items-center gap-1 mt-3 text-xs font-medium"
                    style={{ color: theme.primary }}
                  >
                    {showOverrideReason ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {showOverrideReason ? 'Hide reason' : 'View reason'}
                  </button>
                  {showOverrideReason && (
                    <div className="mt-2 p-3 rounded-lg text-sm" style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}>
                      {assessment.override_reason || 'No reason provided.'}
                    </div>
                  )}
                </div>
              ) : (
                /* Placeholder when no override */
                <div className="p-4 rounded-lg flex items-center justify-center" style={{ backgroundColor: theme.surface, border: `1px dashed ${theme.border}` }}>
                  <p className="text-sm" style={{ color: theme.textMuted }}>No override applied</p>
                </div>
              )}
            </div>
            
            

            {renderConflictSummary()}
          </CardContent>
          <CardFooter>
            <div className="flex items-center gap-3">
              <Button onClick={() => setModalOpen(true)} variant="outline" disabled={isApproved}>
                <Edit className="w-4 h-4 mr-2" /> Override Recommendation
              </Button>
              <Button onClick={handleApprove} disabled={isApproved}>
                <CheckCircle className="w-4 h-4 mr-2" /> {isApproved ? 'Recommendation Approved' : 'Approve Recommendation'}
              </Button>
            </div>
          </CardFooter>
        </Card>
      ) : (
        <Card className="mt-8">
          <CardHeader>
            <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
              <Target className="w-5 h-5" /> Investment Recommendation
            </h3>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <p className="text-lg mb-4" style={{ color: theme.textSecondary }}>
                {assessment_status === 'submitted'
                  ? 'The client has completed the assessment. Generate a recommendation to see the results.'
                  : assessment_status === 'in_progress'
                    ? 'The client is still completing the assessment.'
                    : 'Waiting for the client to complete the assessment.'}
              </p>
              {assessment_status === 'submitted' && (
                <Button onClick={handleGenerateRecommendation} disabled={generating}>
                  {generating ? 'Generating...' : 'Generate Recommendation'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Download Reports */}
      <div className="mt-8 flex justify-end gap-3 flex-wrap">
        <div style={{ position: 'relative' }}>
          <Button
            onClick={handleDownloadArchetypeReport}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Download className="w-4 h-4" />
            Investor Personality Archetype (PDF)
          </Button>
          {showThemePicker && (
            <div
              style={{
                position: 'absolute', bottom: '100%', right: 0, marginBottom: '8px',
                background: '#fff', border: `1px solid ${theme.border}`, borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: '20px', minWidth: '280px', zIndex: 50
              }}
            >
              <p style={{ fontSize: '0.85rem', fontWeight: 600, color: theme.text, marginBottom: '12px' }}>
                Select a theme for the archetype report:
              </p>
              <select
                value={selectedThemeId || ''}
                onChange={e => setSelectedThemeId(parseInt(e.target.value))}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${theme.border}`,
                  fontSize: '0.9rem', background: theme.surface, color: theme.text, marginBottom: '12px'
                }}
              >
                {archetypeThemes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <Button variant="ghost" onClick={() => setShowThemePicker(false)} style={{ padding: '6px 16px', fontSize: '0.85rem' }}>Cancel</Button>
                <Button onClick={handleConfirmArchetypeDownload} style={{ padding: '6px 16px', fontSize: '0.85rem' }}>Download</Button>
              </div>
            </div>
          )}
        </div>
        <Button
          onClick={handleDownloadReport}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Download className="w-4 h-4" />
          Download Compliance Report (PDF)
        </Button>
      </div>
    </>
  );

  // ====== QUOTATION TAB CONTENT ======
  const renderQuotationTab = () => {
    const activeProfile = getActiveRiskProfile();
    const profileColors = {
      conservative: '#3B82F6',
      moderate_conservative: '#8B5CF6',
      moderate: '#10B981',
      moderate_aggressive: '#F59E0B',
      aggressive: '#EF4444'
    };
    const activeColor = profileColors[activeProfile] || theme.primary;

    const investmentVehicles = [
      { value: 'local_unit_trust', label: 'Local Unit Trust' },
      { value: 'offshore_unit_trust', label: 'Offshore Unit Trust' },
      { value: 'retirement_annuity', label: 'Retirement Annuity' },
      { value: 'tax_free_savings', label: 'Tax-Free Savings' },
      { value: 'preservation_fund', label: 'Preservation Fund' },
      { value: 'endowment', label: 'Endowment' }
    ];

    const investmentProviders = [
      { value: 'allan_gray', label: 'Allan Gray' },
      { value: 'ninety_one', label: 'Ninety One' },
      { value: 'glacier', label: 'Glacier' },
      { value: 'sygnia', label: 'Sygnia' },
      { value: 'discovery', label: 'Discovery' },
      { value: 'sanlam', label: 'Sanlam' },
      { value: 'liberty', label: 'Liberty' }
    ];

    const selectedProviders = quotationData.investment_providers || [];

    const toggleProvider = (value) => {
      setQuotationData(prev => {
        const current = prev.investment_providers || [];
        const updated = current.includes(value)
          ? current.filter(p => p !== value)
          : [...current, value];
        return { ...prev, investment_providers: updated };
      });
      setQuotationSaved(false);
    };

    const lines = quotationData.lines || [];

    // Can save if at least one line has a vehicle type and funds totalling 100%
    const canSave = lines.some(line => {
      const hasVehicle = line.investment_type && line.investment_type !== '';
      const funds = line.funds || [];
      const fundTotal = funds.reduce((sum, f) => sum + Number(f.percentage || 0), 0);
      const isFundTotalValid = funds.length === 0 || Math.abs(fundTotal - 100) < 0.01;
      return hasVehicle && funds.length > 0 && isFundTotalValid;
    });

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.text }}>
              <FileBadge className="w-5 h-5" /> Quotation
            </h3>
            <p className="text-sm mt-1" style={{ color: theme.textSecondary }}>
              Configure the client's investment quotation. Add multiple vehicles as needed. Funds are pulled from the House View based on the approved risk profile.
            </p>
          </div>
          <Button
            onClick={handleSaveQuotation}
            disabled={quotationSaving || !canSave}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            {quotationSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {quotationSaved ? 'Saved ✓' : 'Save Quotation'}
          </Button>
        </div>

        {/* Approved strategy summary */}
        <div className="p-4 rounded-xl" style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="w-5 h-5 text-emerald-500" />
            <span className="font-semibold" style={{ color: theme.text }}>Recommendation Approved</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs" style={{ color: theme.textSecondary }}>Client</p>
              <p className="text-sm font-medium" style={{ color: theme.text }}>{client?.full_name}</p>
            </div>
            <div>
              <p className="text-xs" style={{ color: theme.textSecondary }}>Active Risk Profile</p>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: activeColor }} />
                <span className="text-sm font-medium" style={{ color: theme.text }}>{strategyMap[activeProfile] || 'N/A'}</span>
                {assessment?.advisor_override && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-medium">Override</span>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs" style={{ color: theme.textSecondary }}>Strategy</p>
              <p className="text-sm font-medium" style={{ color: theme.text }}>
                {assessment?.advisor_override && assessment?.override_strategy_id
                  ? strategies.find(s => s.id === assessment.override_strategy_id)?.strategy_name
                  : finalStrategy?.strategy_name || recommendedStrategy?.strategy_name || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Investment Firm (multi-select) — shared across all vehicles */}
        <div className="p-4 rounded-xl" style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold" style={{ color: theme.text }}>Investment Firm</p>
            {selectedProviders.length > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${activeColor}20`, color: activeColor }}>
                {selectedProviders.length} selected
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {investmentProviders.map(provider => {
              const isSelected = selectedProviders.includes(provider.value);
              return (
                <button
                  key={provider.value}
                  onClick={() => toggleProvider(provider.value)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all text-left"
                  style={{
                    backgroundColor: isSelected ? `${activeColor}15` : theme.surface,
                    border: `1px solid ${isSelected ? activeColor : theme.border}`,
                    color: isSelected ? activeColor : theme.textSecondary
                  }}
                >
                  <div
                    className="w-4 h-4 rounded flex items-center justify-center text-xs shrink-0"
                    style={{
                      backgroundColor: isSelected ? activeColor : 'transparent',
                      border: `1.5px solid ${isSelected ? activeColor : theme.border}`,
                      color: isSelected ? '#fff' : 'transparent'
                    }}
                  >
                    {isSelected && '✓'}
                  </div>
                  {provider.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Pull from House View (only if not initialized) */}
        {!quotationInitialized && (
          <div className="p-4 rounded-xl" style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
            <div className="text-center py-4">
              <p className="text-sm mb-3" style={{ color: theme.textMuted }}>
                Click below to load funds from House View for the {strategyMap[activeProfile] || 'active'} risk profile.
              </p>
              <Button
                onClick={initializeQuotation}
                disabled={!activeProfile}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', margin: '0 auto' }}
              >
                Pull from House View
              </Button>
            </div>
          </div>
        )}

        {/* Investment Vehicle Lines */}
        {lines.map((line, lineIndex) => {
          const lineFundTotal = getLineFundTotal(lineIndex);
          const isLineFundValid = (line.funds || []).length === 0 || Math.abs(lineFundTotal - 100) < 0.01;
          const vehicleLabel = investmentVehicles.find(v => v.value === line.investment_type)?.label || 'New Vehicle';

          return (
            <div key={lineIndex} className="rounded-xl overflow-hidden" style={{ border: `2px solid ${lineIndex === 0 ? activeColor : theme.border}` }}>
              {/* Line Header */}
              <div className="px-5 py-3 flex items-center justify-between" style={{ backgroundColor: theme.card }}>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-8 rounded-full" style={{ backgroundColor: lineIndex === 0 ? activeColor : theme.textSecondary }} />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: theme.text }}>
                      Vehicle {lineIndex + 1}: {vehicleLabel}
                    </p>
                  </div>
                </div>
                {lines.length > 1 && (
                  <button
                    onClick={() => removeQuotationLine(lineIndex)}
                    className="text-xs px-3 py-1.5 rounded-md font-medium transition-colors"
                    style={{ color: '#ef4444', border: '1px solid #fca5a5', backgroundColor: 'transparent' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    Remove Vehicle
                  </button>
                )}
              </div>

              <div className="px-5 py-4 space-y-4" style={{ backgroundColor: theme.surface }}>
                {/* Investment Vehicle Type */}
                <div>
                  <label className="text-xs font-medium mb-1.5 block" style={{ color: theme.textSecondary }}>Investment Vehicle</label>
                  <select
                    value={line.investment_type || ''}
                    onChange={(e) => updateQuotationLineField(lineIndex, 'investment_type', e.target.value)}
                    className="w-full text-sm px-4 py-2.5 rounded-lg"
                    style={{
                      backgroundColor: theme.card,
                      border: `1px solid ${theme.border}`,
                      color: theme.text
                    }}
                  >
                    <option value="">Select investment vehicle...</option>
                    {investmentVehicles.map(v => (
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                  </select>
                </div>

                {/* Advisor Commission */}
                <div>
                  <p className="text-xs font-semibold mb-2" style={{ color: theme.textSecondary }}>Advisor Commission</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium mb-1.5 block" style={{ color: theme.textSecondary }}>Upfront Commission</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          placeholder="0.00"
                          value={line.upfront_commission || ''}
                          onChange={(e) => updateQuotationLineField(lineIndex, 'upfront_commission', e.target.value)}
                          className="w-28 text-center text-sm font-medium px-3 py-2.5 rounded-lg"
                          style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                        />
                        <span className="text-sm" style={{ color: theme.textSecondary }}>% ex VAT</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1.5 block" style={{ color: theme.textSecondary }}>Ongoing Commission</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          placeholder="0.00"
                          value={line.ongoing_commission || ''}
                          onChange={(e) => updateQuotationLineField(lineIndex, 'ongoing_commission', e.target.value)}
                          className="w-28 text-center text-sm font-medium px-3 py-2.5 rounded-lg"
                          style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                        />
                        <span className="text-sm" style={{ color: theme.textSecondary }}>% ex VAT</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Investment Value */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold" style={{ color: theme.textSecondary }}>Investment Value</p>
                    <select
                      value={line.currency || 'ZAR'}
                      onChange={(e) => updateQuotationLineField(lineIndex, 'currency', e.target.value)}
                      className="text-xs px-2.5 py-1 rounded-md"
                      style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                    >
                      <option value="ZAR">R (ZAR)</option>
                      <option value="USD">$ (USD)</option>
                      <option value="EUR">€ (EUR)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium mb-1.5 block" style={{ color: theme.textSecondary }}>Lump Sum</label>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: theme.textSecondary }}>
                          {line.currency === 'ZAR' ? 'R' : line.currency === 'USD' ? '$' : '€'}
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={line.lump_sum_amount || ''}
                          onChange={(e) => updateQuotationLineField(lineIndex, 'lump_sum_amount', e.target.value)}
                          className="flex-1 text-sm px-3 py-2.5 rounded-lg"
                          style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1.5 block" style={{ color: theme.textSecondary }}>Monthly Debit Order</label>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: theme.textSecondary }}>
                          {line.currency === 'ZAR' ? 'R' : line.currency === 'USD' ? '$' : '€'}
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={line.debit_order_amount || ''}
                          onChange={(e) => updateQuotationLineField(lineIndex, 'debit_order_amount', e.target.value)}
                          className="flex-1 text-sm px-3 py-2.5 rounded-lg"
                          style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                        />
                      </div>
                      {line.debit_order_amount && Number(line.debit_order_amount) > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs" style={{ color: theme.textSecondary }}>Annual escalation:</span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            placeholder="0.0"
                            value={line.debit_order_escalation || ''}
                            onChange={(e) => updateQuotationLineField(lineIndex, 'debit_order_escalation', e.target.value)}
                            className="w-16 text-center text-xs px-2 py-1.5 rounded-md"
                            style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                          />
                          <span className="text-xs" style={{ color: theme.textSecondary }}>% p.a.</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fund Allocation */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold" style={{ color: theme.textSecondary }}>Fund Selection</p>
                    {(line.funds || []).length > 0 && (
                      <div className={`text-xs font-bold px-2.5 py-1 rounded-full ${isLineFundValid ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`}>
                        {lineFundTotal.toFixed(0)}%
                      </div>
                    )}
                  </div>
                  {(line.funds || []).length === 0 ? (
                    <div className="py-4 text-center">
                      <p className="text-xs" style={{ color: theme.textMuted }}>No funds allocated. Add funds below.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {(line.funds || []).map((fund, fundIdx) => (
                        <div key={fundIdx} className="flex items-center gap-3">
                          <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: activeColor }} />
                          <input
                            type="text"
                            placeholder="Fund name..."
                            value={fund.name}
                            onChange={(e) => updateQuotationFund(lineIndex, fundIdx, 'name', e.target.value)}
                            className="flex-1 text-sm px-3 py-2 rounded-lg"
                            style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                          />
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={fund.percentage || ''}
                              onChange={(e) => updateQuotationFund(lineIndex, fundIdx, 'percentage', e.target.value)}
                              placeholder="0"
                              className="w-16 text-center text-sm font-medium px-2 py-2 rounded-lg"
                              style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                            />
                            <span className="text-xs" style={{ color: theme.textSecondary }}>%</span>
                          </div>
                          <button
                            onClick={() => removeQuotationFund(lineIndex, fundIdx)}
                            className="text-xs px-2 py-1.5 rounded-md transition-colors"
                            style={{ color: theme.textSecondary, backgroundColor: 'transparent' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(239,68,68,0.1)'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {(line.funds || []).length < 10 && (
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => addQuotationFund(lineIndex)}
                        className="flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors flex-1"
                        style={{
                          color: activeColor,
                          backgroundColor: 'transparent',
                          border: `1px dashed ${activeColor}40`
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${activeColor}10`}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <span className="text-lg leading-none" style={{ color: activeColor }}>+</span>
                        Add fund
                      </button>
                      <button
                        onClick={() => setHouseViewModalLine(lineIndex)}
                        className="flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors flex-1"
                        style={{
                          color: theme.textSecondary,
                          backgroundColor: 'transparent',
                          border: `1px dashed ${theme.border}`
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${theme.surface}`}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <span className="text-sm leading-none">📋</span>
                        Pull House View
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Add Investment Vehicle button */}
        {quotationInitialized && (
          <button
            onClick={addQuotationLine}
            className="w-full p-4 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
            style={{
              color: activeColor,
              backgroundColor: 'transparent',
              border: `2px dashed ${activeColor}50`
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${activeColor}08`}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span className="text-xl leading-none" style={{ color: activeColor }}>+</span>
            Add Investment Vehicle
          </button>
        )}

        {/* Personality summary (compact) */}
        {personalityProfile && (
          <div className="p-4 rounded-xl" style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
            <p className="text-sm font-semibold mb-3" style={{ color: theme.textSecondary }}>Personality Summary</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {personalityProfile.map((metric) => {
                const colors = getLevelColor(metric.level);
                return (
                  <div key={metric.key} className="p-3 rounded-lg text-center" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                    <p className="text-xs font-medium mb-1" style={{ color: theme.textSecondary }}>{metric.name}</p>
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold" style={{ backgroundColor: colors.bg, color: colors.text }}>{metric.level}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Download buttons */}
        <div className="flex justify-end pt-4 gap-3">
          <Button
            variant="secondary"
            onClick={handleDownloadQuotationPDF}
            disabled={!canSave}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Download className="w-4 h-4" />
            Compliance Report
          </Button>
          <Button
            onClick={handleDownloadQuoteRequest}
            disabled={!canSave}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <FileText className="w-4 h-4" />
            Quote Request
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <header className="border-b" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Assessment Details</h1>
              <p className="text-sm" style={{ color: theme.textSecondary }}>{client?.full_name} - {new Date(assessment.created_at).toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                <FileText className="w-4 h-4 mr-2" /> Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 p-1 rounded-lg" style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
          <button
            onClick={() => setActiveTab('review')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all"
            style={{
              backgroundColor: activeTab === 'review' ? theme.primary : 'transparent',
              color: activeTab === 'review' ? '#fff' : theme.textSecondary
            }}
          >
            <ClipboardList className="w-4 h-4" />
            Review
          </button>
          <button
            onClick={() => { if (isApproved) setActiveTab('quotation'); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all"
            style={{
              backgroundColor: activeTab === 'quotation' ? theme.primary : 'transparent',
              color: isApproved ? (activeTab === 'quotation' ? '#fff' : theme.textSecondary) : theme.textMuted,
              cursor: isApproved ? 'pointer' : 'not-allowed',
              opacity: isApproved ? 1 : 0.5
            }}
          >
            <FileBadge className="w-4 h-4" />
            Quotation
            {!isApproved && <span className="text-xs ml-1">🔒</span>}
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'review' && renderReviewTab()}
        {activeTab === 'quotation' && renderQuotationTab()}

        {/* Override Modal (renders on top of both tabs) */}
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
            <div className="max-w-lg w-full">
              <Card>
                <CardHeader>
                  <h2 className="text-2xl font-bold" style={{ color: theme.text }}>Override Recommendation</h2>
                  <p className="text-sm" style={{ color: theme.textSecondary }}>Select a different strategy. The original recommendation will be preserved.</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={(e) => { e.preventDefault(); handleOverride(); }} noValidate className="space-y-4">
                    <Select
                      label="Select Override Strategy"
                      options={strategies.map(s => ({ value: s.id, label: `${s.strategy_name} (${strategyMap[s.risk_profile]})` }))}
                      value={selectedStrategyId}
                      onChange={(e) => setSelectedStrategyId(e.target.value)}
                      required
                    />
                    <Input
                      label="Reason for Override"
                      as="textarea"
                      rows="4"
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      required
                      error={!overrideReason ? 'Reason is required' : ''}
                    />

                    {/* Client Approved Checkbox */}
                    <label className="flex items-center gap-3 p-3 rounded-lg cursor-pointer" style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}>
                      <input
                        type="checkbox"
                        checked={clientApprovedOverride}
                        onChange={(e) => setClientApprovedOverride(e.target.checked)}
                        className="w-4 h-4 rounded"
                        style={{ accentColor: theme.primary }}
                      />
                      <div>
                        <p className="text-sm font-medium" style={{ color: theme.text }}>Approved by client?</p>
                        <p className="text-xs" style={{ color: theme.textSecondary }}>Tick if the client has agreed to this override</p>
                      </div>
                    </label>

                    <div className="pt-4 flex justify-end space-x-3">
                      <Button type="button" variant="secondary" onClick={() => { setModalOpen(false); setOverrideReason(''); setClientApprovedOverride(false); }}>Cancel</Button>
                      <Button type="submit">Save Override</Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>

      {/* House View Profile Picker Modal */}
      {houseViewModalLine !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="max-w-md w-full">
            <Card>
              <CardHeader>
                <h2 className="text-2xl font-bold" style={{ color: theme.text }}>Pull from House View</h2>
                <p className="text-sm" style={{ color: theme.textSecondary }}>
                  Select a risk profile to pull its fund allocation into Vehicle {houseViewModalLine + 1}.
                  This will replace the current fund selection.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[
                    { key: 'conservative', label: 'Conservative', color: '#3B82F6' },
                    { key: 'moderate_conservative', label: 'Moderate Conservative', color: '#8B5CF6' },
                    { key: 'moderate', label: 'Moderate', color: '#10B981' },
                    { key: 'moderate_aggressive', label: 'Moderate Aggressive', color: '#F59E0B' },
                    { key: 'aggressive', label: 'Aggressive', color: '#EF4444' }
                  ].map(profile => (
                    <button
                      key={profile.key}
                      onClick={() => pullHouseViewForLine(houseViewModalLine, profile.key)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all text-left"
                      style={{
                        backgroundColor: theme.surface,
                        border: `1px solid ${theme.border}`,
                        color: theme.text
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = profile.color}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = theme.border}
                    >
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: profile.color }} />
                      {profile.label}
                    </button>
                  ))}
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button variant="secondary" onClick={() => setHouseViewModalLine(null)}>Cancel</Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50" style={{ animation: 'slideUp 0.3s ease-out' }}>
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg max-w-sm"
            style={{
              backgroundColor: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
              border: `1px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
              color: toast.type === 'error' ? '#dc2626' : '#16a34a',
            }}
          >
            {toast.type === 'error' ? (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-auto flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentDetail;
